import { ExternalLink, Info, MapPin } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalSpecList } from './ProfessionalSpecList';

/**
 * One card in the stack: a pale translucent panel over the banner.
 *
 * Translucent rather than solid, and blurred rather than tinted — the point is
 * that the photograph is still there underneath, softened. A solid panel would
 * be a box sitting on the banner; this reads as frosted glass laid on it.
 */
const CARD = cn(
    'rounded-[1.5rem] bg-white/72 backdrop-blur-[18px] backdrop-saturate-[1.1]',
    'shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_18px_40px_-24px_rgba(0,0,0,0.28)]',
);

const META_ROW =
    'm-0 flex items-center gap-[0.5rem] font-sans text-[1.0625rem] leading-[1.35] tracking-[-0.005em] text-[#141414]';

/**
 * The practice's identity, as a stack of cards straddling the banner's edge.
 *
 * The facts were a column in the summary block below the banner, where they
 * read as the first row of the page's body. As a stack half over the
 * photograph they read as a label on it — which is what they are: who this is,
 * what they do, where they work, and the two things you can do about it.
 *
 * The stack is pulled up by half its own measured height rather than by a
 * fixed offset, because its height depends on how many facts a profile
 * carries. Measuring is what keeps "half over, half below" true for a practice
 * with one location and one with four.
 */
/**
 * Profile details — parked, not deleted.
 *
 * The card stack works, but the banner is not where these belong; they are
 * coming back in some other form. Kept whole behind one flag rather than
 * deleted or commented out, so bringing it back is a one-word change and the
 * component stays under typecheck and lint in the meantime.
 */
const SHOW_PROFILE_DETAILS = false;

export const ProfessionalIdentityStack = () => {
    const initial = useProfessionalInitialRouteData();
    if (!SHOW_PROFILE_DETAILS || !initial) return null;

    const discipline = initial.PrimaryCategory?.PrettyTitle;
    const cities = Array.from(
        new Set(
            (initial.LibraryLocations ?? [])
                .map((location) => location?.City?.trim())
                .filter((city): city is string => Boolean(city)),
        ),
    );
    const websiteHost = initial.Website
        ? initial.Website.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
        : '';

    return (
        // Out of the flow entirely, hung off the banner's bottom-right
        // corner. In flow it pushed everything under it down by half its own
        // height, which moved the tab band off the banner's edge — and the
        // band belongs there.
        //
        // z-130 clears the sticky tab band's 115. The stack overlaps the band
        // by design, so it has to win; below it, the band slid over the card
        // as soon as the page moved.
        <div className="pointer-events-none absolute right-[3rem] bottom-0 z-[130] translate-y-1/2 max-md:hidden">
            <div className="pointer-events-auto flex w-[24rem] flex-col gap-[0.5rem]">
                <div className={cn(CARD, 'relative p-[1.5rem] pt-[3.25rem]')}>
                    {initial.CompanyLogo ? (
                        // Centred on the card's top edge and hanging half
                        // above it, so the mark introduces the stack rather
                        // than sitting inside it as another row.
                        <ProfessionalTileLogo
                            image={{
                                src: initial.CompanyLogo,
                                alt: `${initial.Title} logo`,
                            }}
                            background={initial.LogoBackgroundColor ?? null}
                            className="absolute -top-[3.25rem] left-1/2 size-[6.5rem] -translate-x-1/2"
                        />
                    ) : null}
                    {/* Not a heading: the banner above already carries the
                        page's h1 with this same name. */}
                    {/* The reference sets its title heavier and smaller than
                        we had it — 28px semibold rather than 33px medium — so
                        the card reads as a label on the picture rather than as
                        a second headline competing with the one below. */}
                    <p className="m-0 font-sans font-semibold tracking-[-0.03em] text-[#141414] md:text-[1.75rem] md:leading-[1.15]">
                        {initial.Title}
                    </p>

                    <div className="flex flex-col md:mt-[1rem] md:gap-[0.375rem]">
                        {discipline ? (
                            <p className={META_ROW}>
                                <Info
                                    aria-hidden
                                    className="size-[1.125rem] shrink-0"
                                />
                                {discipline}
                            </p>
                        ) : null}
                        {cities.length > 0 ? (
                            <p className={META_ROW}>
                                <MapPin
                                    aria-hidden
                                    className="size-[1.125rem] shrink-0"
                                />
                                <span>{cities.join(', ')}</span>
                            </p>
                        ) : null}
                        {websiteHost && initial.Website ? (
                            <RoccoLink
                                to={initial.Website}
                                newTab
                                className={cn(META_ROW, 'underline')}
                            >
                                <ExternalLink
                                    aria-hidden
                                    className="size-[1.125rem] shrink-0"
                                />
                                {websiteHost}
                            </RoccoLink>
                        ) : null}
                    </div>
                </div>

                <div className={cn(CARD, 'p-[1.5rem]')}>
                    <ProfessionalSpecList compact />
                </div>

                <div
                    className={cn(
                        CARD,
                        'flex flex-col gap-[0.5rem] p-[1.25rem]',
                    )}
                >
                    {initial.Website ? (
                        <RoccoLink
                            to={initial.Website}
                            newTab
                            className={detailCta(
                                // Hairline, not white: on a white card a white
                                // button is only its own shadow.
                                'outlined-light',
                                'w-full md:h-[3.25rem] md:rounded-[1rem]',
                            )}
                        >
                            Visit Website
                        </RoccoLink>
                    ) : null}
                    <RoccoLink
                        to={`${initial.BaseUrl}/contact`}
                        className={detailCta(
                            // The one thing the card is asking for, filled.
                            'filled-light',
                            'w-full md:h-[3.25rem] md:rounded-[1rem]',
                        )}
                    >
                        Get in touch
                    </RoccoLink>
                </div>
            </div>
        </div>
    );
};
